import React, { useMemo, useRef, useState, useEffect } from 'react';
import { TextElement } from './elements/text-element';
import { SingleLineElement } from './elements/single-line-element';
import { MultilineElement } from './elements/multiline-element';
import { NumberElement } from './elements/number-element';
import { PhoneElement } from './elements/phone-element';
import { EmailElement } from './elements/email-element';
import { FullNameElement } from './elements/full-name-element';
import { AddressElement } from './elements/address-element';
import { DateElement } from './elements/date-element';
import { TimeElement } from './elements/time-element';
import { DropdownElement } from './elements/dropdown-element';
import { CheckboxElement } from './elements/checkbox';
import { RadiobuttonElement } from './elements/radiobutton-element';
import { LinkElement } from './elements/link';
import { RatingElement } from './elements/rating-element';
import { SignatureElement } from './elements/signature-element';
import { TableElement } from './elements/table';
import { SingleLineHeaderElement } from './elements/single-line-header-element';
import { SubmittersFieldElement } from './elements/submitters-field-element';
import { FileUploadElement } from './elements/file-upload-element';
import { FileAttachElement } from './elements/file-attach-element';
import { LoginElement } from './elements/login-element';
import { DividerElement } from './elements/divider';
import { CreditsElement } from './elements/credits-element';
import cx from 'classnames';
import { GridYUnit, GridXUnit, GridYValue } from '../../constants';
import { useIsInitFormPage } from '../../hook/pages/useIsInitFormPage';
import { getRenderedZIndex } from '../../utility/alwaysOnTop';
import { hasFloatingToolbar, legacyTextToolbarShift, LEGACY_TEXT_BUTTON_ROW_SHIFT } from '../../utility/textFieldLayout';

// Field types that render a bordered input and expose the "Print Border" setting.
// Only these get a print-border-* class so the setting never touches other fields.
const PRINT_BORDER_FIELD_TYPES = new Set([
  'single-line',
  'multiline-line',
  'number',
  'name',
  'credits',
  'phone',
  'email',
  'address',
  'date',
  'time',
  'signature',
  'login',
]);

export const FromBuilderItem = function FromElement(props) {
  const {
    handlerChangeFile,
    element,
    handlerChange,
    addNewRow,
    handlerChangeLabel,
    isPrint,
    isSubmitter,
    addNewLogin,
    lastIndex,
    index,
    error,
    setFormFieldsAdditionalHeight,
    formFieldsAdditionalHeight = {},
    duplicateElement,
    removeElement,
    highlightPrevious,
    formHasOnlyOneSubmitter,
    initiatorID,
    isPreview,
    scale,
    submitterAddInfoElementID,
    autoFillingVariables,
    submitterType,
    submitterInfo,
    previewTabItem,
    incrementTop = 0,
    topRelated = 0,
    isMobileView = false,
    strictOrderStep,
    submitter2Info,
    // Print only: a text field that would scroll is shown in full (RenderForm decides).
    isPrintExpanded = false,
  } = props;

  const previewElemRef = useRef();
  const [additionalTop, setAdditionalTop] = useState(0);
  const [additionalHeight, setAdditionalHeight] = useState(0);
  // Room kept above a text field the initiator edits, for its toolbar to hang in.
  const [toolbarReserve, setToolbarReserve] = useState(0);
  const isInitFormPage = useIsInitFormPage();

  // A text field authored before the floating toolbar keeps the room the constructor's
  // button row took above it, so its text stays where its author put it (see
  // utility/textFieldLayout).
  const isLegacyText = element.type === 'text' && !hasFloatingToolbar(element);

  const positionData = useMemo(() => {
    const topShift = isLegacyText ? LEGACY_TEXT_BUTTON_ROW_SHIFT : 0;

    return {
      left: element.options.left - 2 + GridXUnit,
      top: (element.options.top - 2 + incrementTop - topRelated + topShift + additionalTop) * GridYValue + GridYUnit,
      innerElemHeight: (element.options.innerElemHeight + additionalHeight) * GridYValue + GridYUnit,
      width: element.options.width + 4 + GridXUnit,
      height: (element.options.height + (formFieldsAdditionalHeight[element.id] || 0) + toolbarReserve) * GridYValue + GridYUnit,
    };
  }, [
    JSON.stringify(element.options),
    incrementTop,
    additionalTop,
    additionalHeight,
    isLegacyText,
    toolbarReserve,
    JSON.stringify(formFieldsAdditionalHeight),
  ]);

  useEffect(() => {
    // In the mobile (vertical) layout fields flow one under another, so the
    // absolute-positioning offsets computed here are not needed.
    if (isMobileView) return;

    const updateAdditionalTop = () => {
      if (previewElemRef.current?.offsetWidth) {
        if (element.type === 'text') {
          const previewElemWidth = previewElemRef.current?.offsetWidth;
          const { initiator_can_edit: initiatorCanEdit } = element?.options ?? {};
          const currentUserCanEdit = isInitFormPage && initiatorCanEdit;
          const toolbarShift = legacyTextToolbarShift(previewElemWidth);

          // A field authored with the floating toolbar is drawn at its stored `top`,
          // exactly as the constructor draws it: there the toolbar floats above the
          // field instead of sitting inside it, so nothing pushes the text down and the
          // rendered form needs no offset to match. An older field is still drawn with
          // the toolbar's room added back, the way it was authored.
          if (!isLegacyText) {
            // Nothing stands between the field's top edge and its text, so the text is
            // drawn at the field's own `top`. The editable variant hangs its toolbar
            // above that text: the field keeps room for it by starting that much higher
            // and growing by the same amount, so the toolbar is not clipped by the field
            // and the text still lands where the read-only form and the print put it.
            const reserve = currentUserCanEdit ? toolbarShift ?? 0 : 0;

            setAdditionalTop(-reserve);
            setToolbarReserve(reserve);

            return 0;
          }

          if (currentUserCanEdit) {
            setAdditionalTop(-3.8);

            if (toolbarShift !== null) {
              return setAdditionalHeight(toolbarShift + 3.8);
            }

            return 0;
          }

          if (toolbarShift !== null) {
            return setAdditionalTop(toolbarShift);
          }
        } else if (['checkbox', 'table', 'radiobutton'].includes(element.type)) {
          const realHeight = previewElemRef.current.offsetHeight / GridYValue;
          if (realHeight > element.options.height) {
            setFormFieldsAdditionalHeight((state) => ({ ...state, [element.id]: realHeight - element.options.height }));
          }
        }
      }

      return 0;
    };

    const resizeObserver = new ResizeObserver(updateAdditionalTop);
    if (previewElemRef.current) {
      resizeObserver.observe(previewElemRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [setFormFieldsAdditionalHeight, isMobileView, isLegacyText]);

  const marginStyle =
    element.type === 'divider'
      ? element.options.isVisible
        ? {
            minHeight: '2px',
            padding: '20px',
            paddingTop: element.options.marginTop !== undefined ? `${element.options.marginTop}px` : '24px',
            paddingBottom: element.options.marginBottom !== undefined ? `${element.options.marginBottom}px` : '24px',
          }
        : {
            visibility: 'hidden',
            padding: 0,
            paddingTop: element.options.marginTop !== undefined ? `${element.options.marginTop}px` : '24px',
            paddingBottom: element.options.marginBottom !== undefined ? `${element.options.marginBottom}px` : '24px',
          }
      : {
          // The reserve rides on the field's own top padding: it is what the floating
          // toolbar hangs in, above the text and inside the field. The margin settings
          // store what their <input> holds, a string, so take its number before adding
          // the reserve - "24" + 0 is a 240px padding.
          paddingTop: `${Number(element.options.marginTop ?? 24) + toolbarReserve * GridYValue}px`,
          paddingBottom: element.options.marginBottom !== undefined ? `${element.options.marginBottom}px` : '24px',
        };

  return (
    <div
      className={cx('form-builder-element-preview', {
        'show-button': lastIndex === index,
        [`form-builder-element-preview-${element.type}`]: !!element.type,
        'table-form-builder-element-preview': element.type === 'table',
        [`form-label-alignment-${element.options.alignment}`]: !!element.options.alignment,
        [`form-label-${element.options.size}`]: !!element.options.size,
        'form-readonlyy': element.options.readOnly,
        'with-aside-settings': element.options.initiator_can_edit,
        'is-divider': element.type === 'divider',
        // The field keeps room above its text for the editor's toolbar to hang in, so it
        // reaches that much further up than it is drawn - see .has-floating-editor.
        'has-floating-editor': toolbarReserve > 0,
        // Print-only border control (see .print-border-* rules). Only applied while printing
        // (isPrint), so the form is unaffected while displayed/filled. Defaults to 'borders'.
        [`print-border-${element.options.printBorder || 'borders'}`]: isPrint && PRINT_BORDER_FIELD_TYPES.has(element.type),
      })}
      style={
        isMobileView
          ? marginStyle
          : {
              ...marginStyle,
              position: 'absolute',
              left: positionData.left,
              top: positionData.top,
              width: positionData.width,
              // A text that would scroll prints in full: the field keeps its height as its
              // least and grows to the text, and RenderForm moves what it then covers.
              ...(isPrintExpanded ? { height: 'auto', minHeight: positionData.height } : { height: positionData.height }),
              overflowY: element.type === 'text' && !isPrintExpanded ? 'auto' : 'unset',
              zIndex: getRenderedZIndex(element),
            }
      }
      id={`element-${element.id}`}
      data-design-height={parseFloat(positionData.height)}
      // How far the field is drawn below where the form puts it: the room a legacy text keeps
      // for the button row it was authored under. RenderForm reads where it was meant to end.
      data-top-shift={((isLegacyText ? LEGACY_TEXT_BUTTON_ROW_SHIFT : 0) + additionalTop) * GridYValue}
    >
      <div className="form-builder-element-preview-wrapper" ref={previewElemRef}>
        {element.type === 'text' && (
          <TextElement
            element={element}
            error={error}
            handlerChange={handlerChange}
            removeElement={removeElement}
            duplicateElement={duplicateElement}
            autoFillingVariables={autoFillingVariables}
            submitterType={submitterType}
            submitterInfo={submitterInfo}
            isPrint={isPrint}
            isPrintExpanded={isPrintExpanded}
          />
        )}
        {element.type === 'single-line' && (
          <SingleLineElement
            isPrint={isPrint}
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            autoFillingVariables={autoFillingVariables}
            submitterType={submitterType}
            submitterInfo={submitterInfo}
            previewTabItem={previewTabItem}
          />
        )}
        {element.type === 'multiline-line' && (
          <MultilineElement
            isPrint={isPrint}
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            previewTabItem={previewTabItem}
            strictOrderStep={strictOrderStep}
          />
        )}
        {element.type === 'number' && (
          <NumberElement handlerChange={handlerChange} element={element} error={error} formHasOnlyOneSubmitter={formHasOnlyOneSubmitter} />
        )}
        {element.type === 'phone' && (
          <PhoneElement handlerChange={handlerChange} element={element} error={error} formHasOnlyOneSubmitter={formHasOnlyOneSubmitter} />
        )}
        {element.type === 'email' && (
          <EmailElement handlerChange={handlerChange} element={element} error={error} formHasOnlyOneSubmitter={formHasOnlyOneSubmitter} />
        )}
        {element.type === 'name' && (
          <FullNameElement
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
          />
        )}
        {element.type === 'address' && (
          <AddressElement handlerChange={handlerChange} element={element} error={error} formHasOnlyOneSubmitter={formHasOnlyOneSubmitter} />
        )}
        {element.type === 'time' && (
          <TimeElement
            isPrint={isPrint}
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
          />
        )}
        {element.type === 'date' && (
          <DateElement
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            previewTabItem={previewTabItem}
            strictOrderStep={strictOrderStep}
            submitterInfo={submitterInfo}
            submitter2Info={submitter2Info}
          />
        )}
        {element.type === 'dropdown' && (
          <DropdownElement
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            scale={scale}
            previewTabItem={previewTabItem}
          />
        )}
        {element.type === 'checkbox' && (
          <CheckboxElement
            isPrint={isPrint}
            handlerChange={handlerChange}
            element={element}
            error={error}
            duplicateElement={duplicateElement}
            removeElement={removeElement}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            strictOrderStep={strictOrderStep}
          />
        )}
        {element.type === 'radiobutton' && (
          <RadiobuttonElement
            isPrint={isPrint}
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
          />
        )}
        {element.type === 'link' && (
          <LinkElement element={element} handlerChange={handlerChange} error={error} removeElement={removeElement} />
        )}
        {element.type === 'rating' && (
          <RatingElement handlerChange={handlerChange} element={element} error={error} formHasOnlyOneSubmitter={formHasOnlyOneSubmitter} />
        )}
        {element.type === 'credits' && (
          <CreditsElement handlerChange={handlerChange} element={element} error={error} formHasOnlyOneSubmitter={formHasOnlyOneSubmitter} />
        )}

        {element.type === 'signature' && (
          <SignatureElement
            handlerChange={handlerChange}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            scale={scale}
            submitterType={submitterType}
            isPrint={isPrint}
          />
        )}
        {element.type === 'table' && (
          <TableElement
            isPreview={isPreview}
            isPrint={isPrint}
            element={element}
            handlerChangeLabel={handlerChangeLabel}
            handlerChange={handlerChange}
            addNewRow={addNewRow}
            error={error}
            highlightPrevious={highlightPrevious}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
            initiatorID={initiatorID}
          />
        )}
        {element.type === 'single-line-header' && (
          <SingleLineHeaderElement
            element={element}
            handlerChange={handlerChange}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
          />
        )}
        {element.type === 'submitters-field' && (
          <SubmittersFieldElement
            isSubmitter={isSubmitter}
            element={element}
            handlerChange={handlerChange}
            error={error}
            submitterAddInfoElementID={submitterAddInfoElementID}
            strictOrderStep={strictOrderStep}
            submitter2Info={submitter2Info}
          />
        )}
        {element.type === 'file-upload' && (
          <FileUploadElement
            handlerChange={handlerChangeFile}
            element={element}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
          />
        )}
        {element.type === 'file-attach' && (
          <FileAttachElement element={element} error={error} handlerChange={handlerChangeFile} removeElement={removeElement} />
        )}
        {element.type === 'divider' && <DividerElement element={element} />}
        {element.type === 'login' && (
          <LoginElement
            addNewLogin={addNewLogin}
            isSubmitter={isSubmitter}
            element={element}
            handlerChange={handlerChange}
            error={error}
            formHasOnlyOneSubmitter={formHasOnlyOneSubmitter}
          />
        )}
      </div>
    </div>
  );
};
